import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLimit, setSubscriptionLimit] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Define setInitialWorkspace before it's used in useEffect
  const setInitialWorkspace = useCallback((workspace) => {
    setCurrentWorkspace(workspace);
    localStorage.setItem('currentWorkspaceId', workspace.id.toString());
    apiClient.defaults.headers.common['x-workspace-id'] = workspace.id.toString();
    console.log('Set initial workspace:', workspace.name);
  }, []);

  // Set up axios defaults for workspace header
  useEffect(() => {
    const workspaceId = localStorage.getItem('currentWorkspaceId');
    if (workspaceId) {
      apiClient.defaults.headers.common['x-workspace-id'] = workspaceId;
    }
  }, []);

  // Track if workspaces have been loaded to prevent duplicate loads
  const workspacesLoadedRef = useRef(false);
  const lastUserIdRef = useRef(null);

  // Load user's workspaces only when user is authenticated
  useEffect(() => {
    const loadWorkspaces = async () => {
      // Don't load workspaces if auth is still loading
      if (authLoading) {
        return;
      }

      // If user is not authenticated, clear workspace state and stop loading
      if (!user) {
        setWorkspaces([]);
        setCurrentWorkspace(null);
        setLoading(false);
        workspacesLoadedRef.current = false;
        lastUserIdRef.current = null;
        return;
      }

      // Skip if already loaded for this user
      if (workspacesLoadedRef.current && lastUserIdRef.current === user.id) {
        console.log('Workspaces already loaded for user, skipping reload');
        setLoading(false);
        return;
      }

      try {
        console.log('Loading workspaces for authenticated user:', user.email);
        const response = await apiClient.get('/api/workspaces');
        const data = response.data;
        if (data.subscriptionLimit) {
          setWorkspaces(data.workspaces || []);
          setSubscriptionLimit(true);
        } else {
          setWorkspaces(data.workspaces || []);
          setSubscriptionLimit(false);
        }
        workspacesLoadedRef.current = true;
        lastUserIdRef.current = user.id;

        const subRes = await apiClient.get('/api/subscription/current').catch(() => null);
        if (subRes?.data?.success) {
          setSubscriptionInfo(subRes.data);
        } else {
          setSubscriptionInfo(null);
        }

        // Set current workspace from localStorage only if it exists and is valid
        const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
        if (savedWorkspaceId) {
          const list = data.workspaces || [];
          const savedWorkspace = list.find(w => w.id.toString() === savedWorkspaceId);
          if (savedWorkspace) {
            setCurrentWorkspace((prev) => {
              if (prev && prev.id === savedWorkspace.id) {
                return prev;
              }
              apiClient.defaults.headers.common['x-workspace-id'] = savedWorkspaceId;
              console.log('Restored saved workspace:', savedWorkspace.name);
              return savedWorkspace;
            });
          } else {
            // Saved workspace not found, clear it and let user choose
            localStorage.removeItem('currentWorkspaceId');
            delete apiClient.defaults.headers.common['x-workspace-id'];
            console.log('Saved workspace not found, user needs to select a workspace');
          }
        } else {
          // No saved workspace, let user choose
          console.log('No saved workspace, user needs to select a workspace');
        }
      } catch (error) {
        console.error('Failed to load workspaces:', error);
        if (error.response?.status === 401) {
          // User not authenticated, don't show error
          console.log('User not authenticated, skipping workspace load');
        } else {
          console.error('Workspace loading error details:', error.response?.data || error.message);
          // Don't show toast error here as it might be confusing during initial load
          // The WorkspaceSelection component will handle showing appropriate messages
        }
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaces();
  }, [user, authLoading, setInitialWorkspace]); // Depend on user, authLoading, and setInitialWorkspace

  // Clear workspace state when user logs out
  useEffect(() => {
    if (!user && !authLoading) {
      console.log('User logged out, clearing workspace state');
      setWorkspaces([]);
      setCurrentWorkspace(null);
      localStorage.removeItem('currentWorkspaceId');
      delete apiClient.defaults.headers.common['x-workspace-id'];
      setLoading(false);
    }
  }, [user, authLoading]);

  const switchWorkspace = useCallback((workspace) => {
    setCurrentWorkspace(prev => {
      // Prevent switching to same workspace (avoid unnecessary re-renders)
      if (prev && prev.id === workspace.id) {
        console.log('Already in this workspace, skipping switch');
        return prev; // Return same reference to prevent re-render
      }
      
      console.log('🔄 Switching workspace to:', workspace.name);
      localStorage.setItem('currentWorkspaceId', workspace.id.toString());
      apiClient.defaults.headers.common['x-workspace-id'] = workspace.id.toString();
      toast.success(`Switched to ${workspace.name}`);
      return workspace;
    });
  }, []); // No dependencies!

  const createWorkspace = useCallback(async (name, description) => {
    try {
      const response = await apiClient.post('/api/workspaces', {
        name,
        description
      });

      const newWorkspace = response.data.workspace;
      setWorkspaces(prev => [newWorkspace, ...prev]);
      setSubscriptionLimit(false);

      // Switch to the new workspace
      switchWorkspace(newWorkspace);

      toast.success('Workspace created successfully!');
      return { success: true, workspace: newWorkspace };
    } catch (error) {
      const isLimit = error.response?.status === 403 && (error.response?.data?.message || '').toLowerCase().includes('upgrade');
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to create workspace';
      toast.error(message);
      if (isLimit) {
        setSubscriptionLimit(true);
        const subRes = await apiClient.get('/api/subscription/current').catch(() => null);
        if (subRes?.data?.success) setSubscriptionInfo(subRes.data);
        return { success: false, error: message, subscriptionLimit: true };
      }
      return { success: false, error: message };
    }
  }, [switchWorkspace]);

  const updateWorkspace = useCallback(async (workspaceId, updates) => {
    try {
      const response = await apiClient.put(`/api/workspaces/${workspaceId}`, updates);
      
      const updatedWorkspace = response.data.workspace;
      setWorkspaces(prev => 
        prev.map(w => w.id === workspaceId ? updatedWorkspace : w)
      );
      
      setCurrentWorkspace(prev => {
        if (prev?.id === workspaceId) {
          return updatedWorkspace;
        }
        return prev;
      });
      
      toast.success('Workspace updated successfully!');
      return { success: true, workspace: updatedWorkspace };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update workspace';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []); // No dependencies!

  const deleteWorkspace = useCallback(async (workspaceId) => {
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}`);
      
      let newWorkspace = null;
      
      setWorkspaces(prev => {
        const filtered = prev.filter(w => w.id !== workspaceId);
        if (filtered.length > 0) {
          newWorkspace = filtered[0];
        }
        return filtered;
      });
      
      setCurrentWorkspace(prev => {
        if (prev?.id === workspaceId) {
          if (newWorkspace) {
            localStorage.setItem('currentWorkspaceId', newWorkspace.id.toString());
            apiClient.defaults.headers.common['x-workspace-id'] = newWorkspace.id.toString();
            return newWorkspace;
          } else {
            localStorage.removeItem('currentWorkspaceId');
            delete apiClient.defaults.headers.common['x-workspace-id'];
            return null;
          }
        }
        return prev;
      });
      
      toast.success('Workspace deleted successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete workspace';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []); // No dependencies!

  const inviteUser = useCallback(async (workspaceId, email, role) => {
    try {
      await apiClient.post(`/api/workspaces/${workspaceId}/invite`, {
        email,
        role
      });
      
      toast.success('Invitation sent successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send invitation';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const acceptInvitation = useCallback(async (token) => {
    try {
      const response = await apiClient.post('/api/workspaces/invite/accept', {
        token
      });
      
      // Reload workspaces to include the new one
      const workspacesResponse = await apiClient.get('/api/workspaces');
      setWorkspaces(workspacesResponse.data.workspaces);
      
      toast.success('Invitation accepted successfully!');
      return { success: true, workspace: response.data.workspace };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to accept invitation';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const removeMember = useCallback(async (workspaceId, userId) => {
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/members/${userId}`);
      
      toast.success('Member removed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to remove member';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const updateMemberRole = useCallback(async (workspaceId, userId, role) => {
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/members/${userId}/role`, {
        role
      });
      
      toast.success('Member role updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update member role';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const leaveWorkspace = useCallback(async (workspaceId) => {
    try {
      await apiClient.post(`/api/workspaces/${workspaceId}/leave`);
      
      let newWorkspace = null;
      
      setWorkspaces(prev => {
        const filtered = prev.filter(w => w.id !== workspaceId);
        if (filtered.length > 0) {
          newWorkspace = filtered[0];
        }
        return filtered;
      });
      
      setCurrentWorkspace(prev => {
        if (prev?.id === workspaceId) {
          if (newWorkspace) {
            localStorage.setItem('currentWorkspaceId', newWorkspace.id.toString());
            apiClient.defaults.headers.common['x-workspace-id'] = newWorkspace.id.toString();
            return newWorkspace;
          } else {
            localStorage.removeItem('currentWorkspaceId');
            delete apiClient.defaults.headers.common['x-workspace-id'];
            return null;
          }
        }
        return prev;
      });
      
      toast.success('Left workspace successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to leave workspace';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []); // No dependencies!

  const getWorkspaceMembers = useCallback(async (workspaceId) => {
    try {
      const response = await apiClient.get(`/api/workspaces/${workspaceId}/members`);
      return { success: true, members: response.data.members };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to get workspace members';
      return { success: false, error: message };
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Refreshing workspaces...');
      const response = await apiClient.get('/api/workspaces');
      const data = response.data;
      const list = data.workspaces || [];
      setWorkspaces(list);
      if (data.subscriptionLimit) setSubscriptionLimit(true);
      else setSubscriptionLimit(false);
      const subRes = await apiClient.get('/api/subscription/current').catch(() => null);
      if (subRes?.data?.success) setSubscriptionInfo(subRes.data);
      else setSubscriptionInfo(null);

      setCurrentWorkspace(prev => {
        if (prev) {
          const stillExists = list.find(w => w.id === prev.id);
          if (!stillExists) {
            // Current workspace no longer exists, clear it
            localStorage.removeItem('currentWorkspaceId');
            delete apiClient.defaults.headers.common['x-workspace-id'];
            return null;
          }
        }
        return prev;
      });
      
      return { success: true, workspaces: list };
    } catch (error) {
      console.error('Failed to refresh workspaces:', error);
      if (error.response?.status === 401) {
        console.log('User not authenticated, skipping workspace refresh');
      } else {
        toast.error('Failed to refresh workspaces');
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies!

  const clearCurrentWorkspace = useCallback(() => {
    setCurrentWorkspace(null);
    localStorage.removeItem('currentWorkspaceId');
    delete apiClient.defaults.headers.common['x-workspace-id'];
    console.log('Cleared current workspace');
  }, []);

  // const createNowgrayWorkspace = useCallback(async () => {
  //   try {
  //     const response = await apiClient.post('/api/workspaces/create-nowgray');
      
  //     const newWorkspace = response.data.workspace;
  //     setWorkspaces(prev => [newWorkspace, ...prev]);
      
  //     // Switch to the new workspace
  //     switchWorkspace(newWorkspace);
      
  //     toast.success('Nowgray workspace created successfully with WhatsApp credentials migrated!');
  //     return { success: true, workspace: newWorkspace };
  //   } catch (error) {
  //     const message = error.response?.data?.error || 'Failed to create Nowgray workspace';
  //     toast.error(message);
  //     return { success: false, error: message };
  //   }
  // }, [switchWorkspace]);

  const value = useMemo(() => {
    console.log('🔄 WorkspaceContext value recreated - Workspace ID:', currentWorkspace?.id);
    return {
      workspaces,
      currentWorkspace,
      loading: loading || authLoading,
      subscriptionLimit,
      subscriptionInfo,
      canCreateWorkspace: subscriptionInfo ? subscriptionInfo.can_create_workspace !== false : true,
      createWorkspace,
      // createNowgrayWorkspace,
      switchWorkspace,
      updateWorkspace,
      deleteWorkspace,
      inviteUser,
      acceptInvitation,
      removeMember,
      updateMemberRole,
      leaveWorkspace,
      getWorkspaceMembers,
      refreshWorkspaces,
      clearCurrentWorkspace,
      setInitialWorkspace
    };
  }, [
    workspaces,
    currentWorkspace,
    loading,
    authLoading,
    subscriptionLimit,
    subscriptionInfo,
    createWorkspace,
    // createNowgrayWorkspace,
    switchWorkspace,
    updateWorkspace,
    deleteWorkspace,
    inviteUser,
    acceptInvitation,
    removeMember,
    updateMemberRole,
    leaveWorkspace,
    getWorkspaceMembers,
    refreshWorkspaces,
    clearCurrentWorkspace,
    setInitialWorkspace
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
